import { Injectable } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, timer, switchMap, catchError, timeout } from 'rxjs';
import { StorageService } from '../_services/storage.service';
import { environment } from '../../environments/environment';
import { AngularDeviceInformationService } from 'angular-device-information';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from '../_services/auth.service';
import { InactivityService } from './inactivity-check-service';
import { Router } from '@angular/router';
import { apiCategories } from '../_helpers/apiCategorize';
import { MenusService } from '../_services/menu.service';

interface ApiCategory {
  category: string;
  subcategory?: string;
  action: string;
  flow: string;
  step: number;
  businessDomain: string;
 // accessLevel: 'public' | 'authenticated' | 'admin' | 'super-admin';
  dataFlow: 'read' | 'write' | 'read-write';
}

interface MenuHierarchy {
  mainMenu: string;
  mainMenuId: number | null;
  subMenu: string;
  subMenuId: number | null;
  tabMenu: string;
  tabMenuId: number | null;
  formSection: string;
  fullPath: string;
  breadcrumb: string;
  menuDepth: number;
  isWizardStep: boolean;
  wizardStepName: string;
  wizardStepNumber: number;
}

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  userData: any;
  private lastRefreshTime: number = 0;
  private isRefreshing = false;
  private sessionId: string = '';
  private journeyStartTime: number = Date.now();
  private apiCallSequence: number = 0;

  // Add request deduplication
  private recentRequests = new Set<string>();
  private readonly DEDUP_WINDOW_MS = 1000; // 1 second window for deduplication

  private apiCategories = apiCategories;

  private isSessionTerminationEventSent:boolean = false;

  constructor(
    private storageService: StorageService,
    private deviceService: AngularDeviceInformationService,
    private authService: AuthService,
    private inactivityService: InactivityService,
    private router: Router,
    private menusService: MenusService
  ) {
    this.initializeSession();
  }

  private initializeSession(): void {
    let existingSession = sessionStorage.getItem('userJourneySession');
    if (!existingSession) {
      this.sessionId = uuidv4();
      sessionStorage.setItem('userJourneySession', this.sessionId);
      this.journeyStartTime = Date.now();
    } else {
      this.sessionId = existingSession;
    }
  }

  /**
   * Check if URL should be excluded from activity logging
   */
  private shouldExcludeFromLogging(url: string): boolean {
    // Always exclude these URLs from any logging
    const alwaysExcluded = [
      '/crash-report/shield-activity-log',
     // '/audit/pii/log-access',
      "/mo-crm/token",
      "/mo-crm/GetDropdown",
      "/nameScreeningRemark",
      '/mo-crm/GetDetailsApi',  // Don't log but still process with auth headers
      '/icon/',
      '/assets/',
      '/favicon.ico',
      '/country/'
    ];

    if (alwaysExcluded.some(excluded => url.includes(excluded))) {
      return true;
    }

    // Exclude sensitive authentication endpoints (but allow login/logout)
    const sensitiveExcluded = [
      '/auth/refresh',
      '/auth/change-password',
      '/auth/tfa-setup',
      '/auth/tfa-verify',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email'
    ];

    return sensitiveExcluded.some(excluded => url.includes(excluded));
  }

  /**
   * Check if this is an authentication endpoint that needs special handling
   */
  private isAuthenticationEndpoint(url: string): boolean {
    const authEndpoints = [
      '/mo-crm/token',       // Login endpoint (actual login)
      '/auth/logout'         // Logout endpoint
    ];

    return authEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check for duplicate requests within a time window
   * Now includes sequence number in the key to prevent sequence-based duplicates
   */
  private isDuplicateRequest(req: HttpRequest<any>): boolean {
    // Create a more comprehensive key that includes timestamp window
    const timeWindow = Math.floor(Date.now() / this.DEDUP_WINDOW_MS);
    const requestKey = `${req.method}-${req.url}-${this.getRequestBodyHash(req.body)}-${timeWindow}`;

    if (this.recentRequests.has(requestKey)) {
      console.log(`Duplicate request detected: ${req.method} ${req.url}`);
      return true;
    }

    this.recentRequests.add(requestKey);

    // Clean up old entries
    setTimeout(() => {
      this.recentRequests.delete(requestKey);
    }, this.DEDUP_WINDOW_MS * 2); // Double the cleanup time for safety

    return false;
  }

  private isDuplicatedSessionTerminationEventSent(): boolean {
    if (this.isSessionTerminationEventSent) {
      return true;
    }
    this.isSessionTerminationEventSent = true;
    setTimeout(() => {
      this.isSessionTerminationEventSent = false;
    }, this.DEDUP_WINDOW_MS * 2);
    return false;
  }

  /**
   * Enhanced menu hierarchy detection with support for multi-level menus
   */
  private getMenuHierarchy(routePath: string): MenuHierarchy {
    try {
      // Clean the route path
      let cleanPath = routePath;
      if (cleanPath.charAt(0) === '#') {
        cleanPath = cleanPath.slice(1);
      }
      if (cleanPath.indexOf(";") > -1) {
        cleanPath = cleanPath.substr(0, cleanPath.indexOf(";"));
      }
      cleanPath = cleanPath.split('?')[0];

      // Get menu data from service
      const menuData = this.menusService.getMenusItems();

      // Initialize default hierarchy
      const hierarchy: MenuHierarchy = {
        mainMenu: 'NA',
        mainMenuId: null,
        subMenu: 'NA',
        subMenuId: null,
        tabMenu: 'NA',
        tabMenuId: null,
        formSection: 'NA',
        fullPath: cleanPath,
        breadcrumb: cleanPath,
        menuDepth: 0,
        isWizardStep: false,
        wizardStepName: '',
        wizardStepNumber: 0
      };

      if (menuData && menuData.rawMenus) {
        // Find exact match first
        let currentMenu = menuData.rawMenus.find((menu: any) => {
          return menu.MenuLink === cleanPath || menu.MenuLink === '/' + cleanPath;
        });

        // If no exact match, try to find the closest parent match
        if (!currentMenu) {
          const pathSegments = cleanPath.split('/').filter(segment => segment);

          // Try to match progressively shorter paths
          for (let i = pathSegments.length; i > 0; i--) {
            const partialPath = '/' + pathSegments.slice(0, i).join('/');
            currentMenu = menuData.rawMenus.find((menu: any) => {
              return menu.MenuLink === partialPath;
            });
            if (currentMenu) break;
          }
        }

        if (currentMenu) {
          // Build hierarchy from current menu
          this.buildMenuHierarchy(currentMenu, menuData.rawMenus, hierarchy);

          // Detect specific sections based on route patterns
          this.detectSpecialSections(cleanPath, hierarchy);
        }
      }

      // Fallback: extract info from URL if no menu found
      if (hierarchy.mainMenu === 'NA') {
        this.extractHierarchyFromUrl(cleanPath, hierarchy);
      }

      return hierarchy;
    } catch (error) {
      console.error('Error getting menu hierarchy:', error);
      return {
        mainMenu: 'Error',
        mainMenuId: null,
        subMenu: 'Error',
        subMenuId: null,
        tabMenu: 'Error',
        tabMenuId: null,
        formSection: 'Error',
        fullPath: routePath,
        breadcrumb: routePath,
        menuDepth: 0,
        isWizardStep: false,
        wizardStepName: '',
        wizardStepNumber: 0
      };
    }
  }

  /**
   * Build menu hierarchy by traversing parent-child relationships
   */
  private buildMenuHierarchy(currentMenu: any, allMenus: any[], hierarchy: MenuHierarchy): void {
    const menuChain = [];
    let menu = currentMenu;

    // Build chain from current menu up to root
    while (menu) {
      menuChain.unshift(menu);
      menu = allMenus.find(m => m.MenuId === menu.PMenuId);
    }

    // Assign hierarchy based on chain depth
    if (menuChain.length >= 1) {
      hierarchy.mainMenu = menuChain[0].MenuName || menuChain[0].Title || 'NA';
      hierarchy.mainMenuId = menuChain[0].MenuId;
    }

    if (menuChain.length >= 2) {
      hierarchy.subMenu = menuChain[1].MenuName || menuChain[1].Title || 'NA';
      hierarchy.subMenuId = menuChain[1].MenuId;
    }

    if (menuChain.length >= 3) {
      hierarchy.tabMenu = menuChain[2].MenuName || menuChain[2].Title || 'NA';
      hierarchy.tabMenuId = menuChain[2].MenuId;
    }

    hierarchy.menuDepth = menuChain.length;
    hierarchy.breadcrumb = menuChain.map(m => m.MenuName || m.Title).join(' > ');
  }

  /**
   * Detect special sections like wizard steps, tabs, etc.
   */
  private detectSpecialSections(cleanPath: string, hierarchy: MenuHierarchy): void {
    const pathSegments = cleanPath.split('/').filter(segment => segment);

    // Detect merchant creation wizard steps
    if (cleanPath.includes('/merchants/merchant-creation/')) {
      hierarchy.isWizardStep = true;
      const lastSegment = pathSegments[pathSegments.length - 1];

      // Map specific merchant creation steps
      const merchantSteps: { [key: string]: { name: string, step: number } } = {
        'merchant-add': { name: 'Contact Details', step: 1 },
        'merchant-account': { name: 'Account Details', step: 2 },
        'merchant-basic-setup': { name: 'Basic Setup', step: 3 },
        'merchant-mdr': { name: 'MDR Configuration', step: 4 },
        'merchant-kyc': { name: 'KYC Upload', step: 5 },
        'merchantScreening': { name: 'Screening', step: 6 },
        'merchant-remark': { name: 'Remarks', step: 7 },
        'merchant-configuration': { name: 'Configuration', step: 8 }
      };

      if (merchantSteps[lastSegment]) {
        hierarchy.wizardStepName = merchantSteps[lastSegment].name;
        hierarchy.wizardStepNumber = merchantSteps[lastSegment].step;
        hierarchy.formSection = merchantSteps[lastSegment].name;
        hierarchy.tabMenu = merchantSteps[lastSegment].name;
      }
    }

    // Detect customer creation steps
    else if (cleanPath.includes('/customers/customer-creation/')) {
      hierarchy.isWizardStep = true;
      const lastSegment = pathSegments[pathSegments.length - 1];

      const customerSteps: { [key: string]: { name: string, step: number } } = {
        'customer-add': { name: 'Customer Details', step: 1 },
        'customer-kyc': { name: 'KYC Documents', step: 2 },
        'customer-screening': { name: 'Screening', step: 3 }
      };

      if (customerSteps[lastSegment]) {
        hierarchy.wizardStepName = customerSteps[lastSegment].name;
        hierarchy.wizardStepNumber = customerSteps[lastSegment].step;
        hierarchy.formSection = customerSteps[lastSegment].name;
        hierarchy.tabMenu = customerSteps[lastSegment].name;
      }
    }

    // Detect payment gateway sections
    else if (cleanPath.includes('/payment-gateway/')) {
      const lastSegment = pathSegments[pathSegments.length - 1];

      const paymentSections: { [key: string]: string } = {
        'merchant-account-add': 'Account Addition',
        'merchant-account-edit': 'Account Modification',
        'service-provider-setup': 'Service Provider Setup',
        'bank-configuration': 'Bank Configuration',
        'instrument-setup': 'Instrument Setup'
      };

      if (paymentSections[lastSegment]) {
        hierarchy.formSection = paymentSections[lastSegment];
        hierarchy.tabMenu = paymentSections[lastSegment];
      }
    }

    // Detect report sections
    else if (cleanPath.includes('/report/') || cleanPath.includes('-report')) {
      const reportTypes: { [key: string]: string } = {
        'merchant-transaction-report': 'Transaction Report',
        'customer-transaction-report': 'Customer Transaction Report',
        'settlement-report': 'Settlement Report',
        'cma-statement-report': 'CMA Statement Report',
        'audit-trail': 'Audit Trail Report'
      };

      const lastSegment = pathSegments[pathSegments.length - 1];
      if (reportTypes[lastSegment]) {
        hierarchy.formSection = reportTypes[lastSegment];
        hierarchy.tabMenu = reportTypes[lastSegment];
      }
    }
  }

  /**
   * Extract hierarchy from URL structure when menu data is not available
   */
  private extractHierarchyFromUrl(cleanPath: string, hierarchy: MenuHierarchy): void {
    const pathSegments = cleanPath.split('/').filter(segment => segment);

    if (pathSegments.length >= 1) {
      hierarchy.mainMenu = this.formatSegmentName(pathSegments[0]);
    }

    if (pathSegments.length >= 2) {
      hierarchy.subMenu = this.formatSegmentName(pathSegments[1]);
    }

    if (pathSegments.length >= 3) {
      hierarchy.tabMenu = this.formatSegmentName(pathSegments[2]);
    }

    if (pathSegments.length >= 4) {
      hierarchy.formSection = this.formatSegmentName(pathSegments[3]);
    }

    hierarchy.menuDepth = pathSegments.length;
    hierarchy.breadcrumb = pathSegments.map(segment => this.formatSegmentName(segment)).join(' > ');
  }

  /**
   * Format URL segment to readable name
   */
  private formatSegmentName(segment: string): string {
    return segment
      .replace(/-/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  /**
   * Get menu display name by route path (legacy method for backward compatibility)
   */
  private getMenuDisplayName(routePath: string): string {
    const hierarchy = this.getMenuHierarchy(routePath);
    return hierarchy.breadcrumb || hierarchy.mainMenu;
  }

  /**
   * Get full menu details by route path
   */
  private getMenuDetails(routePath: string): any {
    try {
      let cleanPath = routePath;
      if (cleanPath.charAt(0) === '#') {
        cleanPath = cleanPath.slice(1);
      }
      if (cleanPath.indexOf(";") > -1) {
        cleanPath = cleanPath.substr(0, cleanPath.indexOf(";"));
      }
      cleanPath = cleanPath.split('?')[0];

      const menuData = this.menusService.getMenusItems();

      if (menuData && menuData.rawMenus) {
        return menuData.rawMenus.find((menu: any) => {
          return menu.MenuLink === cleanPath || menu.MenuLink === '/' + cleanPath;
        });
      }

      return null;
    } catch (error) {
      console.error('Error getting menu details:', error);
      return null;
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('🔍 HTTP Interceptor - Request URL:', request.url);
    console.log('🔍 HTTP Interceptor - Request Method:', request.method);

    // Check if this is a logout request
    if (this.isAuthenticationEndpoint(request.url)) {
      console.log('🔐 Authentication endpoint detected:', request.url);
      if (request.url.includes('/logout')) {
        console.log('🚪 LOGOUT endpoint detected - will be logged');
      }
    }

    // Handle login endpoints (token only) - no auth header, no logging
    if (request.url.includes('/mo-crm/token') && !request.url.includes('GetDetails')) {
      console.log('🔓 LOGIN endpoint detected - no auth header needed:', request.url);
      return next.handle(request);
    }

    if (request.url.includes('/user/otp')) {
      console.log('🔓 Wallet OTP endpoint detected - no auth header needed:', request.url);
      return next.handle(request);
    }

    // Skip logging for excluded URLs but still add auth header if needed
    const shouldSkipLogging = this.shouldExcludeFromLogging(request.url);
    if (shouldSkipLogging) {
      console.log('⏭️ Skipping logging for excluded URL:', request.url);
      const token = this.storageService.getToken();
      return next.handle(this.attachAuthHeader(request, token));
    }

   // const activityLogUrl = `http://localhost:9998/crash-report/shield-activity-log/save`;
   const activityLogUrl = `${environment.API_URLBLAZE}/crash-report/shield-activity-log/save`;
    const token = this.storageService.getToken();
    this.inactivityService.updateLastActivity();

    const startTime = performance.now();

    // Add debugging log
    console.log(`Intercepting: ${request.method} ${request.url}`);

    // Check if this is a PII audit/unmask call
    const isPiiAuditCall = this.isPiiAuditCall(request.url);

    // Handle token refresh logic with proper error handling
    if (token && this.storageService.isJwtExpired() && !request.url.endsWith('/auth/refresh') && this.canRefreshToken()) {
      this.userData = this.storageService.getUser();
      this.isRefreshing = true;
      this.authService.refreshAccessToken(this.userData).subscribe({
        next: (response: any) => {
          this.storageService.saveUser(response);
          this.isRefreshing = false;
        },
        error: (error) => {
          console.error('Token refresh failed:', error);
          this.isRefreshing = false;
          // Could redirect to login or handle differently
        }
      });
    }

    // If currently refreshing and not a refresh request, delay with timeout
    if (!request.url.endsWith('/auth/refresh') && this.isRefreshing) {
      return timer(200).pipe(
        timeout(5000), // Add 5 second timeout
        switchMap(() => next.handle(this.attachAuthHeader(request, token))),
        catchError((error) => {
          console.error('Request timeout during token refresh:', error);
          this.isRefreshing = false; // Reset flag on timeout
          return next.handle(this.attachAuthHeader(request, token));
        })
      );
    }

    // Enhanced activity logging with multiple exclusion checks
    const shouldLog = request.url !== activityLogUrl &&
                     request.method !== "OPTIONS" &&  // Exclude OPTIONS preflight requests
                     (request.method === "POST" || request.method === "PUT" || request.method === "DELETE" || request.method === "GET") &&
                     !this.shouldExcludeFromLogging(request.url) &&
                     !this.isDuplicateRequest(request);  // Check duplicates BEFORE incrementing sequence

    if (shouldLog) {
      // Only increment sequence for legitimate, non-duplicate requests
      this.apiCallSequence++;
      console.log(`Logging activity for: ${request.method} ${request.url} (Sequence: ${this.apiCallSequence})`);

      // Special handling for PII audit calls
      if (isPiiAuditCall) {
        try {
          this.logPiiAuditActivity(request, token, startTime);
        } catch (error) {
          console.error('Error in PII audit logging:', error);
        }
      } else {
        try {
          this.logSimplifiedActivity(request, token, startTime);
        } catch (error) {
          console.error('Error in activity logging:', error);
        }
      }
    } else {
      console.log(`Skipping log for: ${request.method} ${request.url} (Reason: ${request.method === 'OPTIONS' ? 'OPTIONS request' : 'Excluded URL or duplicate'})`);
    }

    return next.handle(this.attachAuthHeader(request, token)).pipe(
      catchError((error) => {
        // Simplified error logging - avoid duplicate checks in error scenarios
        if (request.url !== activityLogUrl &&
            request.method !== "OPTIONS" &&
            !this.shouldExcludeFromLogging(request.url)) {
          try {
            if (error.status === 401 && !this.isDuplicatedSessionTerminationEventSent()) {
              console.log("Session Termination from: ", request.method, request.url);
              const sessionTerminationReq = request.clone();
              this.logSimplifiedActivityWithMethod(sessionTerminationReq, token, performance.now() - startTime, 'SESSION TERMINATION');
            }
            this.logApiError(request, error, performance.now() - startTime);
          } catch (logError) {
            console.error('Error logging API error:', logError);
          }
        }
        throw error;
      })
    );
  }

  /**
   * Create a simple hash of request body for deduplication
   */
  private getRequestBodyHash(body: any): string {
    if (!body) return 'no-body';
    try {
      return btoa(JSON.stringify(body)).substring(0, 10);
    } catch {
      return 'hash-error';
    }
  }

  /**
   * Check if this is a PII audit/unmask call
   */
  private isPiiAuditCall(url: string): boolean {
    const piiAuditPatterns = [
      '/audit/pii/log-access',
      '/audit/pii/access-history',
      '/user/get-reasons-by-role',
      'pii-audit',
      'unmask',
      'decrypt-pii',
      'log-access'
    ];

    return piiAuditPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Log PII audit activity with enhanced metadata
   */
  private logPiiAuditActivity(req: HttpRequest<any>, token: string | null, startTime: number): void {
    try {
      // Use setTimeout to make this non-blocking
      setTimeout(() => {
        this.performPiiAuditLog(req, token, startTime);
      }, 0);
    } catch (error) {
      console.error('Error in PII audit logging:', error);
    }
  }

  /**
   * Perform the actual PII audit logging
   */
  private performPiiAuditLog(req: HttpRequest<any>, token: string | null, startTime: number): void {
    const deviceInfo = JSON.parse(JSON.stringify(this.deviceService.getDeviceInfo()));
    let uid = localStorage.getItem("uuid");

    if (uid == null) {
      uid = uuidv4();
      localStorage.setItem("uuid", uid ?? "");
    }

    // Get current route and enhanced menu hierarchy
    const currentRoute = this.router.url;
    const menuHierarchy = this.getMenuHierarchy(currentRoute);
    let menuDetails = null;

    try {
      menuDetails = this.getMenuDetails(currentRoute);
    } catch (error) {
      console.error('Error getting menu details:', error);
    }

    // Get API category info with fallback
    let apiInfo = null;
    try {
      apiInfo = this.getCategoryByUrl(currentRoute);
    } catch (error) {
      console.error('Error getting API category:', error);
    }

    // Get user info
    let userInfo = '';
    if (token) {
      try {
        const jwt = jwtDecode<any>(token);
        userInfo = jwt.username;
      } catch (e) {
        userInfo = 'decode_error';
      }
    }

    const currentTime = Date.now();
    const timeSinceLastCall = this.getTimeSinceLastCall();

    // Extract PII-specific information from request body
    const piiAuditData = this.extractPiiAuditData(req.body);

    const piiAuditPayload = {
      // Original data
      ...deviceInfo,
      uuid: uid,
      url: req.url,
      method: this.getPiiAuditMethod(req.url, piiAuditData), // Enhanced method for PII operations
      user: userInfo,

      // Journey data
      sessionId: this.sessionId,
      journeyStartTime: this.journeyStartTime,
      apiSequenceNumber: this.apiCallSequence,
      timeSinceJourneyStart: currentTime - this.journeyStartTime,
      timeSinceLastCall: timeSinceLastCall,

      // API categorization - Use actual page category, not PII_AUDIT
      category: apiInfo?.category || menuHierarchy.mainMenu,
      action: this.getPiiAuditAction(req.url, piiAuditData),
      flowType: apiInfo?.flow || 'DATA_MANAGEMENT',
      stepNumber: apiInfo?.step || 1,
      businessDomain: apiInfo?.businessDomain || 'CUSTOMER_MANAGEMENT',
      //accessLevel: apiInfo?.accessLevel || 'admin',
      dataFlow: 'read-sensitive',

      // Enhanced Menu Hierarchy Information
      mainMenu: menuHierarchy.mainMenu,
      mainMenuId: menuHierarchy.mainMenuId,
      subMenu: menuHierarchy.subMenu,
      subMenuId: menuHierarchy.subMenuId,
      tabMenu: menuHierarchy.tabMenu,
      tabMenuId: menuHierarchy.tabMenuId,
      formSection: menuHierarchy.formSection,
      menuBreadcrumb: menuHierarchy.breadcrumb,
      menuDepth: menuHierarchy.menuDepth,
      isWizardStep: menuHierarchy.isWizardStep,
      wizardStepName: menuHierarchy.wizardStepName,
      wizardStepNumber: menuHierarchy.wizardStepNumber,

      // Legacy menu fields for backward compatibility
      currentRoute: menuHierarchy.breadcrumb,
      currentRouteDisplayName: menuHierarchy.breadcrumb,
      menuId: menuDetails?.MenuId || menuHierarchy.mainMenuId,
      menuName: menuDetails?.MenuName || menuHierarchy.mainMenu,
      menuTitle: menuDetails?.Title || menuHierarchy.mainMenu,
      menuIcon: menuDetails?.Icon || null,
      menuPosition: menuDetails?.Position || null,
      parentMenuId: menuDetails?.PMenuId || null,
      menuPermissions: menuDetails?.PermissionAction || null,

      // PII-specific metadata
      piiAuditType: this.determinePiiAuditType(req.url),
      fieldName: piiAuditData?.fieldName || 'NA',
      targetUserId: piiAuditData?.userId || 'NA',
      auditReason: piiAuditData?.reasonId || 'NA',
      auditReasonDescription: piiAuditData?.reasonDescription || 'NA',
      auditComment: piiAuditData?.comment || '',
      accessedBy: piiAuditData?.accessedBy || userInfo,
      piiContext: piiAuditData?.context || 'NA', // field vs cell context

      // Simplified entity context for audit trail
      entityType: piiAuditData?.entityType || 'NA',
      entityMobile: piiAuditData?.entityMobile || 'NA',

      // Enhanced payload information for PII auditing
      payload: {
        body: this.sanitizePiiRequestBody(req.body),
        queryParams: this.extractQueryParams(req.url),
        pathParams: this.extractPathParams(req.url)
      },

      // Context data
      referrerRoute: this.getPreviousRoute(),

      // Enhanced metrics for PII
      sessionDuration: currentTime - this.journeyStartTime,
      isNewSession: this.apiCallSequence === 1,
      requestPayloadSize: req.body ? JSON.stringify(req.body).length : 0,
      hasAuthentication: !!token,
      isHighRiskOperation: true, // All PII operations are high risk
      complianceLevel: 'GDPR_COMPLIANT',

      // Timestamp
      timestamp: currentTime,
      createDate: new Date().toISOString(),

      // Additional audit trail metadata
      auditTrail: {
        eventType: 'PII_ACCESS',
        riskLevel: 'HIGH',
        requiresApproval: false,
        retentionPeriod: '7_YEARS',
        dataClassification: 'CONFIDENTIAL'
      },

      // Enhanced metadata for user activity audit interface report
      operationType: this.determinePiiAuditType(req.url),
      operationDescription: this.getOperationDescription(req.url, piiAuditData),
      userAction: this.getPiiAuditMethod(req.url, piiAuditData),
      targetResource: `${piiAuditData?.fieldName || 'PII_DATA'} (${piiAuditData?.context || 'field'})`,
      sourceModule: menuHierarchy.mainMenu
    };

    // Store last call time for next calculation
    localStorage.setItem('lastApiCallTime', currentTime.toString());
    const payloadJson = JSON.stringify(piiAuditPayload);
    console.log("🔒 Sending PII audit log via sendBeacon:", payloadJson);

    // Use sendBeacon for reliable delivery
    const success = window.navigator.sendBeacon(
     //  `http://localhost:9998/crash-report/shield-activity-log/save`,
    `${environment.API_URLBLAZE}/crash-report/shield-activity-log/save`,
      payloadJson
    );

    if (!success) {
      console.warn('⚠️ PII Audit SendBeacon failed, request may not have been sent');
    }
  }

  /**
   * Determine the type of PII audit operation
   */
  private determinePiiAuditType(url: string): string {
    if (url.includes('log-access')) return 'ACCESS_LOG';
    if (url.includes('access-history')) return 'HISTORY_QUERY';
    if (url.includes('get-reasons')) return 'REASON_LOOKUP';
    if (url.includes('unmask')) return 'UNMASK_REQUEST';
    if (url.includes('decrypt')) return 'DECRYPT_REQUEST';
    return 'AUDIT_OPERATION';
  }

  /**
   * Get enhanced method for PII audit operations
   */
  private getPiiAuditMethod(url: string, piiAuditData: any): string {
    if (url.includes('log-access')) {
      return 'UNMASK'; // For PII unmask operations
    }
    if (url.includes('access-history')) {
      return 'VIEW'; // For viewing PII access history
    }
    if (url.includes('get-reasons')) {
      return 'VIEW'; // For viewing audit reasons
    }

    // Default based on audit data context
    if (piiAuditData?.context === 'field') {
      return 'UNMASK_FIELD';
    } else if (piiAuditData?.context === 'cell') {
      return 'UNMASK_CELL';
    }

    return 'VIEW'; // Default fallback
  }

  /**
   * Get PII audit action based on URL and data
   */
  private getPiiAuditAction(url: string, piiAuditData: any): string {
    if (url.includes('log-access')) {
      const fieldName = piiAuditData?.fieldName || 'PII_DATA';
      const context = piiAuditData?.context || 'field';
      return `UNMASK_${fieldName.toUpperCase()}_${context.toUpperCase()}`;
    }
    if (url.includes('access-history')) {
      return 'VIEW_PII_HISTORY';
    }
    if (url.includes('get-reasons')) {
      return 'GET_AUDIT_REASONS';
    }

    return 'PII_OPERATION';
  }

  /**
   * Get page category from current route
   */
  private getPageCategoryFromRoute(route: string): string {
    const routeLower = route.toLowerCase();

    if (routeLower.includes('customer')) {
      return 'Customer Management';
    } else if (routeLower.includes('merchant')) {
      return 'Merchant Management';
    } else if (routeLower.includes('reseller')) {
      return 'Reseller Management';
    } else if (routeLower.includes('user')) {
      return 'User Management';
    } else if (routeLower.includes('audit')) {
      return 'Audit Management';
    } else if (routeLower.includes('report')) {
      return 'Reporting';
    } else if (routeLower.includes('transaction')) {
      return 'Transaction Management';
    } else if (routeLower.includes('payment')) {
      return 'Payment Management';
    } else if (routeLower.includes('dashboard')) {
      return 'Dashboard';
    }

    return 'General Management';
  }

  /**
   * Get operation description for audit reporting
   */
  private getOperationDescription(url: string, piiAuditData: any): string {
    if (url.includes('log-access')) {
      const fieldName = piiAuditData?.fieldName || 'PII data';
      const context = piiAuditData?.context || 'field';
      const entityType = piiAuditData?.entityType || 'entity';
      return `User unmasked ${fieldName} ${context} for ${entityType} record`;
    }
    if (url.includes('access-history')) {
      return 'User viewed PII access history';
    }
    if (url.includes('get-reasons')) {
      return 'User retrieved audit reasons for PII access';
    }

    return 'User performed PII-related operation';
  }

  /**
   * Get standard method name for audit reporting
   * Maps HTTP methods to business-friendly action names for user activity interface
   */
  private getStandardMethod(httpMethod: string): string {
    switch (httpMethod.toUpperCase()) {
      case 'GET':
        return 'VIEW';
      case 'POST':
        return 'ADD/EDIT';
      case 'PUT':
        return 'UPDATE';
      case 'DELETE':
        return 'DELETE';
      case 'PATCH':
        return 'MODIFY';
      case 'HEAD':
        return 'CHECK';
      case 'OPTIONS':
        return 'OPTIONS';
      default:
        // For any unknown HTTP method, return it in uppercase
        return httpMethod.toUpperCase();
    }
  }

  /**
   * Get enhanced method for specific operations based on URL patterns
   */
  private getEnhancedMethod(httpMethod: string, url: string): string {
    const urlLower = url.toLowerCase();

    // Check for authentication operations first
    if (this.isAuthenticationEndpoint(url)) {
      console.log(`🔐 Authentication endpoint detected: ${url}`);

      if (urlLower.includes('/mo-crm/token')) {
        console.log(`🔑 LOGIN detected for: ${url}`);
        return 'LOGIN';
      }
      if (urlLower.includes('/auth/logout')) {
        console.log(`🚪 LOGOUT detected for: ${url}`);
        return 'LOGOUT';
      }
    }

    // Special case: GetDetails should be logged as LOGIN (but still gets auth headers)
    if (urlLower.includes('/mo-crm/getdetails') && !urlLower.includes('getdetailsapi')) {
      console.log(`🔑 LOGIN method detected for GetDetails: ${url}`);
      return 'LOGIN';
    }

    // Check for download-related URLs
    if (urlLower.includes('/download') ||
        urlLower.includes('/export') ||
        urlLower.includes('/csv') ||
        urlLower.includes('/excel') ||
        urlLower.includes('/qr') ||
        urlLower.includes('/pdf') ||
        urlLower.includes('download-uploadfiles') ||
        urlLower.includes('getCustomerPdfById')) {
      return 'DOWNLOAD';
    }

    // Check for PII-related operations
    if (urlLower.includes('log-access') || urlLower.includes('unmask')) {
      return 'UNMASK';
    }

    if (urlLower.includes('unmask') && urlLower.includes('field')) {
      return 'UNMASK_FIELD';
    }

    if (urlLower.includes('unmask') && urlLower.includes('cell')) {
      return 'UNMASK_CELL';
    }

    // Fall back to standard HTTP method mapping
    return this.getStandardMethod(httpMethod);
  }

  /**
   * Extract PII audit data from request body
   */
  private extractPiiAuditData(body: any): any {
    if (!body) return {};

    try {
      return {
        fieldName: body.fieldName || body.field_name || null,
        userId: body.userId || body.user_id || null,
        reasonId: body.reasonId || body.reason_id || null,
        reasonDescription: body.reasonDescription || body.reason_description || null,
        comment: body.comment || null,
        accessedBy: body.accessedBy || body.accessed_by || null,
        context: body.context || null, // field vs cell context

        // Simplified entity context
        entityType: body.entityType || body.entity_type || null,
        entityMobile: body.entityMobile || body.entity_mobile || null
      };
    } catch (error) {
      return {};
    }
  }

  /**
   * Sanitize PII request body while preserving audit fields
   */
  private sanitizePiiRequestBody(body: any): any {
    if (!body) return null;

    // Create a copy to avoid modifying the original
    const sanitized = { ...body };

    // For PII audit calls, preserve audit fields but remove sensitive data
    const sensitiveFields = ['password', 'token', 'refresh_token', 'access_token', 'secret', 'key', 'credential'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Keep PII audit fields for compliance
    const piiAuditFields = ['fieldName', 'userId', 'reasonId', 'comment', 'accessedBy', 'timestamp'];
    const auditData: any = {};
    piiAuditFields.forEach(field => {
      if (body[field]) {
        auditData[field] = body[field];
      }
    });

    return { ...sanitized, auditData };
  }

  /**
   * Sanitize authentication request body to remove sensitive information
   */
  private sanitizeAuthRequestBody(body: any): any {
    if (!body) return null;

    const sanitized = { ...body };

    // Remove sensitive fields
    const sensitiveFields = [
      'password', 'pwd', 'pass', 'token', 'accessToken', 'refreshToken',
      'authorization', 'auth', 'secret', 'key', 'apiKey', 'clientSecret',
      'username', 'user', 'email', 'emailId', 'mobile', 'phone'
    ];

    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Handle nested objects
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeAuthRequestBody(sanitized[key]);
      }
    });

    return sanitized;
  }

  private logSimplifiedActivity(req: HttpRequest<any>, token: string | null, startTime: number): void {
    try {
      // Use setTimeout to make this non-blocking
      setTimeout(() => {
        this.performSimplifiedActivityLog(req, token, startTime);
      }, 0);
    } catch (error) {
      console.error('Error in activity logging:', error);
    }
  }

  private logSimplifiedActivityWithMethod(req: HttpRequest<any>, token: string | null, startTime: number, method: string): void {
    try {
      // Use setTimeout to make this non-blocking
      setTimeout(() => {
        this.performSimplifiedActivityLog(req, token, startTime, method);
      }, 0);
    } catch (error) {
      console.error('Error in activity logging with custom method:', error);
    }
  }

  /**
   * Perform the actual simplified activity logging
   */
  private performSimplifiedActivityLog(req: HttpRequest<any>, token: string | null, startTime: number, customMethod?: string): void {
    const deviceInfo = JSON.parse(JSON.stringify(this.deviceService.getDeviceInfo()));
    let uid = localStorage.getItem("uuid");

    if (uid == null) {
      uid = uuidv4();
      localStorage.setItem("uuid", uid ?? "");
    }

    // Get current route and enhanced menu hierarchy
    const currentRoute = this.router.url;
    const menuHierarchy = this.getMenuHierarchy(currentRoute);
    let menuDetails = null;

    try {
      menuDetails = this.getMenuDetails(currentRoute);
    } catch (error) {
      console.error('Error getting menu details:', error);
    }

    // Get API category info with fallback
    let apiInfo = null;
    try {
      apiInfo = this.getCategoryByUrl(currentRoute);
    } catch (error) {
      console.error('Error getting API category:', error);
    }

    // Get user info (but be careful with sensitive auth data)
    let userInfo = '';
    if (token) {
      try {
        const jwt = jwtDecode<any>(token);
        userInfo = jwt.username;
      } catch (e) {
        userInfo = 'decode_error';
      }
    }

    const currentTime = Date.now();
    const timeSinceLastCall = this.getTimeSinceLastCall();

    const simplifiedPayload = {
      // Original data
      ...deviceInfo,
      uuid: uid,
      url: req.url,
      method: customMethod || this.getEnhancedMethod(req.method, req.url),
      user: userInfo,

      // Journey data
      sessionId: this.sessionId,
      journeyStartTime: this.journeyStartTime,
      apiSequenceNumber: this.apiCallSequence,
      timeSinceJourneyStart: currentTime - this.journeyStartTime,
      timeSinceLastCall: timeSinceLastCall,

      // API categorization
      category: apiInfo?.category || menuHierarchy.mainMenu,
      action: apiInfo?.action,
      flowType: apiInfo?.flow,
      stepNumber: apiInfo?.step,
      businessDomain: apiInfo?.businessDomain,
   //   accessLevel: apiInfo?.accessLevel,
      dataFlow: apiInfo?.dataFlow,

      // Enhanced Menu Hierarchy Information
      mainMenu: menuHierarchy.mainMenu,
      mainMenuId: menuHierarchy.mainMenuId,
      subMenu: menuHierarchy.subMenu,
      subMenuId: menuHierarchy.subMenuId,
      tabMenu: menuHierarchy.tabMenu,
      tabMenuId: menuHierarchy.tabMenuId,
      formSection: menuHierarchy.formSection,
      menuBreadcrumb: menuHierarchy.breadcrumb,
      menuDepth: menuHierarchy.menuDepth,
      isWizardStep: menuHierarchy.isWizardStep,
      wizardStepName: menuHierarchy.wizardStepName,
      wizardStepNumber: menuHierarchy.wizardStepNumber,

      // Legacy menu fields for backward compatibility
      currentRoute: menuHierarchy.breadcrumb,
      currentRouteDisplayName: menuHierarchy.breadcrumb,
      menuId: menuDetails?.MenuId || menuHierarchy.mainMenuId,
      menuName: menuDetails?.MenuName || menuHierarchy.mainMenu,
      menuTitle: menuDetails?.Title || menuHierarchy.mainMenu,
      menuIcon: menuDetails?.Icon || null,
      menuPosition: menuDetails?.Position || null,
      parentMenuId: menuDetails?.PMenuId || null,
      menuPermissions: menuDetails?.PermissionAction || null,

      // Simplified payload information (sanitized for non-auth requests)
      payload: {
        body: this.sanitizeRequestBody(req.body, req.url),
        queryParams: this.extractQueryParams(req.url),
        pathParams: this.extractPathParams(req.url)
      },

      // Context data
      referrerRoute: this.getPreviousRoute(),

      // Basic metrics
      sessionDuration: currentTime - this.journeyStartTime,
      isNewSession: this.apiCallSequence === 1,
      requestPayloadSize: req.body ? JSON.stringify(req.body).length : 0,
      hasAuthentication: !!token,

      // Timestamp
      timestamp: currentTime,
      createDate: new Date().toISOString()
    };

    // Store last call time for next calculation
    localStorage.setItem('lastApiCallTime', currentTime.toString());
    const payloadJson = JSON.stringify(simplifiedPayload);
    console.log("📊 Sending enhanced activity log via sendBeacon:", payloadJson);

    // Use sendBeacon for reliable delivery
    const success = window.navigator.sendBeacon(
     //  `http://localhost:9998/crash-report/shield-activity-log/save`,
      `${environment.API_URLBLAZE}/crash-report/shield-activity-log/save`,
      payloadJson
    );

    if (!success) {
      console.warn('⚠️ SendBeacon failed, request may not have been sent');
    }
  }

  /**
   * Sanitize request body to remove sensitive information
   */
  private sanitizeRequestBody(body: any, url?: string): any {
    if (!body) return null;

    // Use comprehensive authentication sanitization for auth endpoints
    if (url && this.isAuthenticationEndpoint(url)) {
      return this.sanitizeAuthRequestBody(body);
    }

    // Create a copy to avoid modifying the original
    const sanitized = { ...body };

    // Remove sensitive fields for regular endpoints
    const sensitiveFields = ['password', 'token', 'refresh_token', 'access_token', 'secret', 'key', 'credential'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  private extractQueryParams(url: string): { [key: string]: string } {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params: { [key: string]: string } = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });

    return params;
  }

  private extractPathParams(url: string): string[] {
    return url.split('/').filter(segment => segment && !segment.includes('?'));
  }

  private getApiInfo(url: string): any {
    for (const pattern in this.apiCategories) {
      if (url.includes(pattern)) {
        return this.apiCategories[pattern];
      }
    }

    return {
      category: 'NA',
      action: 'API Call',
      flow: 'General Flow',
      step: 0
    };
  }

  /**
 * Enhanced getCategoryByUrl method with better URL matching
 */
getCategoryByUrl(url: string): ApiCategory | null {
  // Extract path from full URL if it contains domain
  let cleanUrl = url;

  // Remove protocol and domain if present
  if (url.includes('://')) {
    try {
      const urlObj = new URL(url);
      cleanUrl = urlObj.pathname;
    } catch (e) {
      // If URL parsing fails, use the original approach
      cleanUrl = url;
    }
  }

  // Remove leading slash if present
  cleanUrl = cleanUrl.startsWith('/') ? cleanUrl.substring(1) : cleanUrl;

  // Remove query parameters and fragments
  cleanUrl = cleanUrl.split('?')[0].split('#')[0];

  console.log(`Trying to categorize cleaned URL: ${cleanUrl}`);

  // 1. Direct exact match
  if (this.apiCategories[cleanUrl]) {
    console.log(`Direct match found for: ${cleanUrl}`);
    return this.apiCategories[cleanUrl];
  }

  // 2. Try with leading slash
  const slashUrl = '/' + cleanUrl;
  if (this.apiCategories[slashUrl]) {
    console.log(`Slash match found for: ${slashUrl}`);
    return this.apiCategories[slashUrl];
  }

  // 3. Pattern matching for dynamic routes with parameters/wildcards
  for (const pattern in this.apiCategories) {
    if (this.matchesPattern(cleanUrl, pattern) || this.matchesPattern(slashUrl, pattern)) {
      console.log(`Pattern match found: ${pattern} for URL: ${cleanUrl}`);
      return this.apiCategories[pattern];
    }
  }

  // 4. Hierarchical matching - try parent routes (most specific first)
  const segments = cleanUrl.split('/').filter(segment => segment);

  // Try matching from most specific to least specific
  for (let i = segments.length; i > 0; i--) {
    const parentRoute = segments.slice(0, i).join('/');

    // Check direct match
    if (this.apiCategories[parentRoute]) {
      console.log(`Hierarchical match found: ${parentRoute} for URL: ${cleanUrl}`);
      return this.apiCategories[parentRoute];
    }

    // Check with leading slash
    if (this.apiCategories['/' + parentRoute]) {
      console.log(`Hierarchical slash match found: /${parentRoute} for URL: ${cleanUrl}`);
      return this.apiCategories['/' + parentRoute];
    }

    // Check pattern matching for parent routes
    for (const pattern in this.apiCategories) {
      if (this.matchesPattern(parentRoute, pattern) || this.matchesPattern('/' + parentRoute, pattern)) {
        console.log(`Hierarchical pattern match found: ${pattern} for parent: ${parentRoute}`);
        return this.apiCategories[pattern];
      }
    }
  }

  // 5. Fallback: check if any pattern contains key segments from the URL
  const urlSegments = cleanUrl.split('/');
  for (const pattern in this.apiCategories) {
    const patternSegments = pattern.split('/');

    // Check if URL contains all non-wildcard segments from pattern
    const isMatch = patternSegments.every(segment => {
      if (segment === '*' || segment.startsWith(':')) return true;
      return urlSegments.includes(segment);
    });

    if (isMatch && patternSegments.length > 1) {
      console.log(`Segment-based match found: ${pattern} for URL: ${cleanUrl}`);
      return this.apiCategories[pattern];
    }
  }

  console.log(`No match found for URL: ${cleanUrl}`);
  return {
    category: 'NA',
    action: 'API Call',
    flow: 'General Flow',
    step: 0,
    businessDomain: 'NA',
   // accessLevel: 'admin',
    dataFlow: 'read-write'
  };
}

/**
 * Enhanced pattern matching method
 */
private matchesPattern(url: string, pattern: string): boolean {
  // Exact match
  if (url === pattern) {
    return true;
  }

  // Wildcard matching
  if (pattern.includes('*')) {
    const regexPattern = pattern.replace(/\*/g, '.*');
    return new RegExp(`^${regexPattern}$`).test(url);
  }

  // Parameter matching (e.g., :id)
  if (pattern.includes(':')) {
    const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${regexPattern}$`).test(url);
  }

  // Partial matching (if URL starts with pattern or vice versa)
  if (url.startsWith(pattern + '/') || pattern.startsWith(url + '/')) {
    return true;
  }

  // Check if pattern is a substring of the URL (for nested routes)
  if (url.includes(pattern) && pattern.length > 3) { // Avoid matching very short patterns
    return true;
  }

  return false;
}

  private logApiError(req: HttpRequest<any>, error: any, responseTime: number): void {
    const currentRoute = this.router.url;
    const menuHierarchy = this.getMenuHierarchy(currentRoute);

    const errorPayload = {
      sessionId: this.sessionId,
      url: req.url,
      method: this.getEnhancedMethod(req.method, req.url), // Use business-friendly method name with URL context
      errorType: 'API_ERROR',
      errorStatus: error.status,
      errorMessage: error.message,
      responseTime: responseTime,
      timestamp: Date.now(),
      user: this.getCurrentUser(),

      // Enhanced menu hierarchy for error context
      mainMenu: menuHierarchy.mainMenu,
      subMenu: menuHierarchy.subMenu,
      tabMenu: menuHierarchy.tabMenu,
      formSection: menuHierarchy.formSection,
      menuBreadcrumb: menuHierarchy.breadcrumb,

      payload: {
        body: this.sanitizeRequestBody(req.body, req.url),
        queryParams: this.extractQueryParams(req.url),
        pathParams: this.extractPathParams(req.url)
      }
    };

    window.navigator.sendBeacon(
      `${environment.API_URLBLAZE}/crash-report/shield-activity-log/error`,
      JSON.stringify(errorPayload)
    );
  }

  private getTimeSinceLastCall(): number {
    const lastCallTime = localStorage.getItem('lastApiCallTime');
    if (!lastCallTime) return 0;
    return Date.now() - parseInt(lastCallTime);
  }

  private getPreviousRoute(): string {
    return sessionStorage.getItem('previousRoute') || '';
  }

  private getCurrentUser(): string {
    const token = this.storageService.getToken();
    if (token) {
      try {
        const jwt = jwtDecode<any>(token);
        return jwt.username;
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  private attachAuthHeader(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    const headers: any = token ? { Authorization: `Bearer ${token}` } : {};
    return req.clone({ setHeaders: headers });
  }

  private canRefreshToken(): boolean {
    const currentTime = Date.now();
    if (currentTime - this.lastRefreshTime < 60000) return false;
    this.lastRefreshTime = currentTime;
    return true;
  }
}

export const HttpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }
];
